// ✅ src/pages/Vehicle/MainVehicleTypeListing.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)
// Backend mount:
//   app.use("/api/mainTypeListing", mainVehicleTypeListing);
//
// Endpoint used:
//   GET https://nissi-india.com/api/mainTypeListing?search=&page=1&limit=10
//
// Notes:
// - Sends localStorage values in headers:
//     x-finyearid, x-finyear, x-branchid, x-branch, x-userid
// - Clicking Vehicle Type opens SubType listing in new tab with querystring:
//     /vehicle/MainVehicleTypeSubTypeListing?vehicletypeid=ID
// - "Add New" navigates to /vehicle/VehicleTypeMainMaster (change if your route differs)

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaEye } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/mainTypeListing";

const MainVehicleTypeListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const fetchData = async (customSearch = search, customPage = page) => {
    setLoading(true);
    setErr("");

    try {
      const res = await axios.get(API_BASE, {
        params: {
          search: customSearch,
          page: customPage,
          limit,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setRows(res.data.data || []);
        setTotalPages(Number(res.data.totalPages || 1));
      } else {
        setRows([]);
        setTotalPages(1);
        setErr(res.data?.message || "No records found");
      }
    } catch (e) {
      console.error("mainTypeListing fetch error:", e);
      setRows([]);
      setTotalPages(1);
      setErr("Failed to fetch vehicle types");
      toast.error("Failed to fetch vehicle types!");
    } finally {
      setLoading(false);
    }
  };

  // ✅ initial + pagination + refetch when headers change
  useEffect(() => {
    fetchData(search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, commonHeaders]);

  const onSearchClick = () => {
    setPage(1);
    fetchData(search, 1);
  };

  const clearFilters = () => {
    setSearch("");
    setPage(1);
    fetchData("", 1);
  };

  const openSubType = (vehTypeId) => {
    if (!vehTypeId) return;
    window.open(`/vehicle/MainVehicleTypeSubTypeListing?vehicletypeid=${vehTypeId}`, "_blank");
  };

  const handleEdit = (row) => {
    toast.info(`Editing: ${row.vehicleType || ""}`);
    navigate(`/vehicle/VehicleTypeMainMaster?id=${row.id}`);
  };

  const handleView = (row) => {
    toast.info(`Viewing: ${row.vehicleType || ""}`);
    openSubType(row.id);
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE MAIN TYPE LISTING</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Types
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-white" : "bg-blue-100 text-blue-800"
              }`}
            >
              {loading ? "..." : `${rows.length}`} Records
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white" + (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700" + (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate("/vehicle/VehicleTypeMainMaster")}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaPlus className="w-4 h-4" />
              Add New Vehicle Type
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-5 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Search Vehicle Types
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSearchClick();
                    }}
                    placeholder="Type to search vehicle types..."
                    className={`pl-10 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={clearFilters}
                className="text-gray-600 dark:text-gray-300 font-medium"
              >
                Clear All Filters
              </button>

              <button
                onClick={onSearchClick}
                className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg transition-all duration-200`}
              >
                <FaSearch />
                Search Records
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {err && !loading ? (
            <div className="text-center py-10">
              <div className="mb-2 text-red-500 font-semibold">{err}</div>
              <button
                onClick={() => fetchData(search, page)}
                className={`inline-flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-5 py-2 rounded-lg`}
              >
                <FaSearch /> Retry
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-left font-semibold">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold">Vehicle Type</th>
                    <th className="px-4 py-4 text-left font-semibold">Index</th>
                    <th className="px-4 py-4 text-center font-semibold">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    Array.from({ length: limit }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-3/4" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-1/4" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                        No Vehicle Types Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr
                        key={row.id ?? idx}
                        className={`${isDark ? "bg-gray-900 text-white" : "bg-white text-gray-900"} border-t ${
                          isDark ? "border-gray-800" : "border-gray-200"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium">
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-4 py-3">
                          <button
                            onClick={() => openSubType(row.id)}
                            className="text-blue-400 hover:text-blue-300 cursor-pointer font-medium underline text-left"
                            title="Open subtype listing"
                          >
                            {row.vehicleType}
                          </button>
                        </td>

                        <td className="px-4 py-3 text-sm font-medium">{row.index ?? ""}</td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(row)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200"
                            >
                              <FaEdit />
                              Edit
                            </button>

                            <button
                              onClick={() => handleView(row)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-all duration-200"
                            >
                              <FaEye />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-white" : "text-gray-700"}`}>
              Page <span className="font-semibold">{page}</span> of{" "}
              <span className="font-semibold">{totalPages}</span>
            </div>

            <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
                className={`px-4 py-2 text-sm font-medium ${
                  page === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "text-white"
                    : "text-gray-700"
                }`}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    page === i + 1
                      ? `bg-${baseColor}-${baseShade} text-white`
                      : isDark
                      ? "text-white"
                      : "text-gray-700"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
                className={`px-4 py-2 text-sm font-medium ${
                  page === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "text-white"
                    : "text-gray-700"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default MainVehicleTypeListing;